import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';
import forEach from '../../../utils/accessors/forEach';
import get from '../../../utils/accessors/get';
import throwError from '../../../utils/error/#throwError';

const virtualized = new WeakSet();

export default function exposeVirtuals<State extends object, ScopedState extends object = State>(state: ScopedState, store: AugmentedStore<State>) {
  if (virtualized.has(state)) {
    return state;
  }

  const proxy = Array.isArray(state) ? [] : {};

  virtualized.add(proxy);

  Object.keys(state).forEach((key) => {
    const value = (state as Record<string, unknown>)[key];

    if (typeof value === 'object' && value !== null && !key.startsWith('[') && isPlainObject(value)) {
      Object.defineProperty(proxy, key, {
        configurable: true,
        enumerable: true,
        get: (() => {
          let cache: Record<string, unknown>;
          return () => {
            if (cache) {
              return cache;
            }
            cache = exposeVirtuals<State, typeof value>(value, store);
            return cache;
          };
        })(),
        set: () => { warnOfMutation(key); },
      });
    } else if (typeof value !== 'undefined') {
      Object.defineProperty(proxy, key, {
        configurable: true,
        enumerable: true,
        get: () => value,
        set: () => { warnOfMutation(key); },
      });
    }
  });

  exposeActions<State>(proxy as State, store);
  exposeSelectors<State>(proxy as State, store);

  Object.seal(proxy);

  return proxy;
}

function warnOfMutation(key: string) {
  throwError(`WithStore: Trying to set "${key}" property on immutable state. Please clone the state before mutating.`);
}

function isPlainObject(object: object) {
  return Array.isArray(object) || object.constructor === Object;
}

function exposeActions<State extends object>(state: State, store: AugmentedStore<State>) {
  const actions = get(state, '[actions]') as Record<string, string>;

  forEach(actions, (actionKey, actionName) => {
    Object.defineProperty(state, actionName, {
      configurable: true,
      value: store.actionStore[actionKey],
      enumerable: false,
    });
  });

  return state;
}

function exposeSelectors<State extends object>(state: State, store: AugmentedStore<State>) {
  const selectors = get(state, '[selectors]') as Record<string, string>;

  forEach(selectors, (selectorKey, selectorName) => {
    Object.defineProperty(state, selectorName, {
      configurable: true,
      enumerable: false,
      get: (() => {
        let selector: unknown;
        return () => {
          if (selector) {
            return selector;
          }

          const selectorCreator = store.selectorStore[selectorKey];

          if (selectorCreator) {
            const fullState: object = store.getState();
            selector = selectorCreator(fullState);
            return selector;
          }

          return undefined;
        };
      })(),
    });
  });

  return state;
}
